import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import ReactSelect from 'react-select';
import StepNavigation from '../../components/StepNavigation/StepNavigation';
import LoadingScreen from '../../components/LoadingScreen/LoadingScreen';
import { useSimulationBank } from '../../../../../providers/SimulationBank/SimulationBankProvider';
import { DigitacaoFormField } from '../../../../../models/SimulationBank/SimulationBankModels';
import { applyMoneyMask, parseMoney } from '../Step4Params/moneyUtils';
import { applyMaskCNPJ } from '../utils/masks';
import { selectStyles } from '../Step4Params/selectStyles';
import { groupFieldsIntoTabs, getFieldMask, getNestedValue, getFieldGroup, resolveSelectOptionValue } from './digitacaoFormUtils';
import {
    filterVisibleDigitacaoFields,
    findEstadoCivilField,
    isConjugeField,
    isEstadoCivilCasado,
    detectFieldKind,
    isCidadeReaproveitadaDaJornada,
    validateTelefoneCelular,
    validateDataEmissao,
    validateCidadeViaCep,
    findTipoDocumentoField,
    isDocumentoNumeroField,
    isTipoDocumentoCnh,
    getDocumentoFieldText,
    getDocumentoFieldsToReset,
    maskDocumentoNumero,
    validateDocumentoNumero,
    getDigitacaoTabLabel,
} from './digitacaoFieldRules';

interface StepDigitacaoFormProps {
    onAvancar: () => void;
    onBack: () => void;
}

const LOADING_FORM_STATUS_MESSAGES = [
    'Carregando o formulário de digitação',
    'Buscando as exigências do banco selecionado',
    'Quase lá!',
];

const VALIDATION_STATUS_MESSAGES = [
    'Validando as informações digitadas',
    'Verificando a adequação para envio ao banco',
    'Quase lá!',
];

const StepDigitacaoForm: FC<StepDigitacaoFormProps> = ({ onAvancar, onBack }) => {
    const {
        digitacaoForm, isLoadingDigitacaoForm, handleLoadDigitacaoForm,
        formDataDigitacao, setFormDataDigitacao, handleValidateDigitacao, isValidatingDigitacao,
        ofertaSelecionada,
    } = useSimulationBank();
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [moneyDisplay, setMoneyDisplay] = useState<Record<string, string>>({});
    const [maskedDisplay, setMaskedDisplay] = useState<Record<string, string>>({});
    const [viaCepCidadePorGrupo, setViaCepCidadePorGrupo] = useState<Record<string, string | null>>({});

    const { register, control, trigger, getValues, setValue, setError, clearErrors, reset, watch, formState: { errors } } = useForm({
        mode: 'onChange',
        defaultValues: formDataDigitacao ?? {},
    });

    useEffect(() => {
        handleLoadDigitacaoForm();
        reset({});
        setActiveTabIndex(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ofertaSelecionada?.id]);

    // Restaura dados já digitados (ex.: usuário voltou da tela de Confirmação) assim que
    // o schema chega, já que os campos ainda não existiam no form no primeiro render.
    // AI-1109: campos reaproveitados/fixos/removidos (ver digitacaoFieldRules.ts) não são
    // exibidos como entrada editável — o valor deles é injetado direto no payload pelo
    // provider, sem depender de o campo estar registrado aqui no form.
    const visibleFields = useMemo(
        () => filterVisibleDigitacaoFields(digitacaoForm ?? [], ofertaSelecionada?.financeira ?? ''),
        [digitacaoForm, ofertaSelecionada?.financeira]
    );

    useEffect(() => {
        if (!digitacaoForm || !formDataDigitacao) return;
        reset(formDataDigitacao);
        const money: Record<string, string> = {};
        const masked: Record<string, string> = {};
        visibleFields.forEach((f) => {
            const value = getNestedValue(formDataDigitacao, f.chave);
            if (value == null) return;

            if (f.type === 'number') {
                money[f.chave] = Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            } else {
                const mask = getFieldMask(f.chave);
                if (mask) {
                    masked[f.chave] = mask(String(value));
                }
            }
        });
        setMoneyDisplay(money);
        setMaskedDisplay(masked);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [digitacaoForm, visibleFields]);

    // CPF e nome do cônjuge só são obrigatórios quando o estado civil é "Casado" (ver
    // isEstadoCivilCasado); nos demais casos seguem opcionais, como vem no schema.
    const estadoCivilField = useMemo(() => findEstadoCivilField(visibleFields), [visibleFields]);
    const estadoCivilValue = estadoCivilField ? watch(estadoCivilField.chave as any) : undefined;
    const isCasado = isEstadoCivilCasado(estadoCivilField, estadoCivilValue);
    const isFieldRequired = (field: DigitacaoFormField): boolean =>
        field.required || (isCasado && isConjugeField(field.chave));

    // Data de emissão é validada contra a data de nascimento atual do formulário, quando o
    // schema também pedir esse campo — se não pedir, só a checagem "não pode ser futura"
    // se aplica (ver validateDataEmissao).
    const dataNascimentoField = useMemo(
        () => visibleFields.find((f) => detectFieldKind(f.chave) === 'data_nascimento'),
        [visibleFields]
    );
    const dataNascimentoValue = dataNascimentoField ? watch(dataNascimentoField.chave as any) : undefined;

    useEffect(() => {
        if (isCasado) return;
        visibleFields.filter((f) => isConjugeField(f.chave)).forEach((f) => clearErrors(f.chave as any));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCasado]);

    // Tipo de documento (RG/CNH, hoje só na VCTEX) muda rótulo, placeholder e máscara dos
    // campos do bloco do RG; ao trocar de tipo, o que foi digitado pro anterior é limpo
    // (ver getDocumentoFieldsToReset). A troca vinda da restauração do form (valor vazio
    // antes/depois) não limpa nada.
    const tipoDocumentoField = useMemo(() => findTipoDocumentoField(visibleFields), [visibleFields]);
    const tipoDocumentoValue = tipoDocumentoField ? watch(tipoDocumentoField.chave as any) : undefined;
    const previousTipoDocumento = useRef<unknown>(tipoDocumentoValue);

    useEffect(() => {
        const previous = previousTipoDocumento.current;
        previousTipoDocumento.current = tipoDocumentoValue;
        if (!previous || !tipoDocumentoValue || previous === tipoDocumentoValue) return;

        getDocumentoFieldsToReset(visibleFields).forEach((f) => {
            setValue(f.chave as any, '');
            clearErrors(f.chave as any);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tipoDocumentoValue]);

    const tabs = useMemo(
        () =>
            groupFieldsIntoTabs(visibleFields).map((tab) => ({
                ...tab,
                label: getDigitacaoTabLabel(tab, ofertaSelecionada?.financeira ?? ''),
            })),
        [visibleFields, ofertaSelecionada?.financeira]
    );
    const activeTab = tabs[activeTabIndex];
    const isLastTab = activeTabIndex === tabs.length - 1;

    const goToNextTab = async () => {
        // Só valida os campos da aba atual ao avançar; a validação completa (todas as
        // abas), mais a validação no back (se os dados estão adequados pro banco), só
        // acontece na última, antes de seguir para a Confirmação.
        const valid = isLastTab ? await trigger() : await trigger(activeTab.fields.map((f) => f.chave) as any);
        if (!valid) return;
        if (!isLastTab) {
            setActiveTabIndex((i) => i + 1);
            return;
        }

        const values = getValues();
        const { success, fieldErrors } = await handleValidateDigitacao(values);
        if (!success) {
            let firstErrorTabIndex: number | null = null;
            fieldErrors.forEach((fieldError) => {
                const field = (digitacaoForm ?? []).find((f) => f.chave === fieldError.field || f.chave.endsWith(`.${fieldError.field}`));
                if (!field) return;
                setError(field.chave as any, { type: 'server', message: fieldError.message });
                const tabIndex = tabs.findIndex((t) => t.fields.some((f) => f.chave === field.chave));
                if (firstErrorTabIndex === null && tabIndex >= 0) firstErrorTabIndex = tabIndex;
            });
            if (firstErrorTabIndex !== null) setActiveTabIndex(firstErrorTabIndex);
            return;
        }

        setFormDataDigitacao(values);
        onAvancar();
    };

    const goBack = () => {
        if (activeTabIndex === 0) onBack();
        else setActiveTabIndex((i) => i - 1);
    };

    // Busca o endereço no ViaCEP (API pública, sem autenticação) e preenche os campos
    // irmãos do CEP no mesmo grupo (logradouro, bairro, cidade, estado/uf), casando o
    // nome retornado com o innerText das opções quando o campo for um select.
    const preencherEnderecoPorCep = async (cepField: DigitacaoFormField, cepValue: string) => {
        const digits = cepValue.replace(/\D/g, '');
        const group = getFieldGroup(cepField.chave);
        // validar a cidade atual contra o retorno de um CEP anterior
        setViaCepCidadePorGrupo((prev) => ({ ...prev, [group]: null }));
        if (digits.length !== 8) return;

        let data: any;
        try {
            const resp = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
            data = await resp.json();
        } catch {
            return;
        }
        if (!data || data.erro) return;

        setViaCepCidadePorGrupo((prev) => ({ ...prev, [group]: data.localidade ?? null }));

        const siblings = visibleFields.filter((f) => getFieldGroup(f.chave) === group);

        const preencherCampo = (field: DigitacaoFormField | undefined, valor?: string) => {
            if (!field || !valor) return;
            if (field.type === 'select') {
                const match = resolveSelectOptionValue(field, valor);
                if (match != null) setValue(field.chave as any, match, { shouldValidate: true });
            } else {
                setValue(field.chave as any, valor, { shouldValidate: true });
            }
        };

        preencherCampo(siblings.find((f) => f.chave.endsWith('.logradouro')), data.logradouro);
        preencherCampo(siblings.find((f) => f.chave.endsWith('.bairro')), data.bairro);
        preencherCampo(siblings.find((f) => f.chave.endsWith('.cidade')), data.localidade);
        preencherCampo(siblings.find((f) => f.chave.endsWith('.estado') || f.chave.endsWith('.uf')), data.uf);
    };

    const renderField = (field: DigitacaoFormField, error: any) => {
        const kind = detectFieldKind(field.chave);
        const { label, placeholder } = getDocumentoFieldText(field, tipoDocumentoValue);
        // Telefone precisa ser celular e data de emissão precisa ser coerente com o
        // nascimento pra qualquer financeira — são regras genéricas, sem exceção
        // confirmada até agora. Cidade×CEP fica fora só pra BMG/FACTA, que reaproveitam a
        // cidade da jornada em vez de usar o campo ligado ao CEP (ver
        // isCidadeReaproveitadaDaJornada).
        const rules = {
            required: isFieldRequired(field) ? `${label} é obrigatório` : false,
            ...(field.pattern ? { pattern: { value: new RegExp(field.pattern), message: field.title || `${label} inválido` } } : {}),
            ...(kind === 'telefone' ? { validate: validateTelefoneCelular } : {}),
            ...(kind === 'data_emissao' ? { validate: (v: string) => validateDataEmissao(v, dataNascimentoValue) } : {}),
            ...(kind === 'cidade' && !isCidadeReaproveitadaDaJornada(ofertaSelecionada?.financeira ?? '', field.chave)
                ? { validate: (v: unknown) => validateCidadeViaCep(field, v, viaCepCidadePorGrupo[getFieldGroup(field.chave)] ?? null) }
                : {}),
        };

        if (field.type === 'select') {
            const options = (field.option ?? []).map((o) => ({ value: o.value, label: o.innerText }));
            return (
                <Controller
                    name={field.chave as any}
                    control={control}
                    rules={rules}
                    render={({ field: { value, onChange } }) => (
                        <ReactSelect
                            options={options}
                            value={options.find((o) => o.value === value) ?? null}
                            styles={selectStyles}
                            placeholder={placeholder || 'Selecione...'}
                            noOptionsMessage={() => 'Sem opções'}
                            onChange={(v: any) => onChange(v?.value ?? '')}
                            className={error ? 'invalid' : ''}
                        />
                    )}
                />
            );
        }

        if (field.type === 'number') {
            return (
                <input
                    type="text"
                    inputMode="decimal"
                    className={error ? 'invalid' : ''}
                    placeholder={field.placeholder}
                    value={moneyDisplay[field.chave] ?? ''}
                    onChange={(e) => {
                        const masked = applyMoneyMask(e.target.value);
                        setMoneyDisplay((prev) => ({ ...prev, [field.chave]: masked }));
                        setValue(field.chave as any, parseMoney(masked), { shouldValidate: true });
                    }}
                    onBlur={() => trigger(field.chave as any)}
                />
            );
        }

        // CNPJ: mostra formatado (00.000.000/0000-00) mas registra só os dígitos — o
        // pattern do schema da financeira valida a contagem de dígitos (ex.: \d{14}), e
        // os separadores da máscara quebravam essa checagem (AI-1304/Daycoval Prêmio).
        if (field.chave.includes('cnpj')) {
            return (
                <Controller
                    name={field.chave as any}
                    control={control}
                    rules={rules}
                    render={({ field: { value, onChange } }) => (
                        <input
                            type="text"
                            inputMode="numeric"
                            className={error ? 'invalid' : ''}
                            placeholder={field.placeholder}
                            value={applyMaskCNPJ(value ?? '')}
                            onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 14))}
                            onBlur={() => trigger(field.chave as any)}
                        />
                    )}
                />
            );
        }

        // Número do documento quando o schema pede o tipo (RG/CNH): a máscara e a validação
        // de tamanho acompanham o tipo selecionado (ver maskDocumentoNumero). Na CNH o
        // pattern do schema sai: ele é o do RG (5 a 15 caracteres, mensagem "RG inválido") e
        // disparava antes da regra dos 11 dígitos, que já basta pra CNH.
        if (tipoDocumentoField && isDocumentoNumeroField(field.chave)) {
            const isCnh = isTipoDocumentoCnh(tipoDocumentoValue);
            const { pattern, ...rulesSemPattern } = rules;
            return (
                <Controller
                    name={field.chave as any}
                    control={control}
                    rules={{
                        ...(isCnh ? rulesSemPattern : rules),
                        validate: (v: string) => validateDocumentoNumero(v, tipoDocumentoValue),
                    }}
                    render={({ field: { value, onChange } }) => (
                        <input
                            type="text"
                            inputMode={isCnh ? 'numeric' : 'text'}
                            className={error ? 'invalid' : ''}
                            placeholder={placeholder}
                            value={value ?? ''}
                            onChange={(e) => onChange(maskDocumentoNumero(e.target.value, tipoDocumentoValue))}
                            onBlur={() => trigger(field.chave as any)}
                        />
                    )}
                />
            );
        }

        const mask = getFieldMask(field.chave);
        if (mask) {
            // Campos com máscara usam setValue sem register, então as regras não rodam
            // neles. O CPF do cônjuge precisa ser registrado pra obrigatoriedade condicional
            // (e o pattern do schema) valerem; o telefone entra pelo mesmo motivo, pra
            // validateTelefoneCelular acima rodar.
            if (isConjugeField(field.chave) || kind === 'telefone') register(field.chave as any, rules);
            return (
                <input
                    type={field.type}
                    className={error ? 'invalid' : ''}
                    placeholder={field.placeholder}
                    value={maskedDisplay[field.chave] ?? ''}
                    onChange={(e) => {
                        const masked = mask(e.target.value);
                        setMaskedDisplay((prev) => ({ ...prev, [field.chave]: masked }));
                        setValue(field.chave as any, masked);
                        if (field.chave.endsWith('.cep')) preencherEnderecoPorCep(field, masked);
                    }}
                    onBlur={() => trigger(field.chave as any)}
                />
            );
        }

        return (
            <input
                type={field.type}
                className={error ? 'invalid' : ''}
                placeholder={placeholder}
                {...register(field.chave as any, rules)}
            />
        );
    };

    if (isValidatingDigitacao) {
        return <LoadingScreen title={<>Estamos validando<br />sua proposta!</>} messages={VALIDATION_STATUS_MESSAGES} />;
    }

    if (isLoadingDigitacaoForm) {
        return <LoadingScreen title={<>Carregando o<br />formulário de digitação!</>} messages={LOADING_FORM_STATUS_MESSAGES} />;
    }

    // Busca terminou mas não trouxe dados (ex.: erro reportado por toast) — mantém uma
    // saída pro usuário em vez de travar numa tela de loading eterna.
    if (!digitacaoForm || !activeTab) {
        return (
            <div className="step-cards">
                <div className="step-cards__polling-notice">
                    <span className="step-cards__polling-dot me-2" />
                    Não foi possível carregar o formulário de digitação.
                </div>
                <StepNavigation onBack={onBack} onNext={() => {}} nextDisabled hideBack={false} />
            </div>
        );
    }

    const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
        // Só bloqueia o Enter em campos de texto/select (evita submit implícito ou
        // acionar sem querer o botão "Voltar"/aba enquanto digita). Num botão focado
        // (ex.: abas, opções), deixa o Enter funcionar normalmente, igual ao Space.
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'BUTTON') {
            e.preventDefault();
        }

        // Mantém a navegação por Tab presa dentro do formulário — sem isso o foco
        // escapa para elementos da tela por trás (sidebar, header do chat, etc.)
        // assim que passa do último campo visível.
        if (e.key === 'Tab') {
            const form = e.currentTarget;
            const focusable = Array.from(
                form.querySelectorAll<HTMLElement>(
                    'input, select, textarea, button, [tabindex]:not([tabindex="-1"]), [href]'
                )
            ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);

            if (focusable.length === 0) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement as HTMLElement;

            if (e.shiftKey) {
                if (active === first || !form.contains(active)) {
                    e.preventDefault();
                    last.focus();
                }
            } else if (active === last || !form.contains(active)) {
                e.preventDefault();
                first.focus();
            }
        }
    };

    return (
        <form className="sim-form" onSubmit={(e) => e.preventDefault()} onKeyDown={handleFormKeyDown} noValidate>
            <div className="digitacao-tabs">
                {tabs.map((tab, i) => (
                    <button key={tab.key} type="button" className={i === activeTabIndex ? 'active' : ''} onClick={() => setActiveTabIndex(i)}>
                        {i + 1}. {tab.label}
                    </button>
                ))}
            </div>

            {tabs.map((tab, tabIdx) => (
                <div
                    className={`digitacao-tab-content ${tabIdx === activeTabIndex ? '' : 'digitacao-tab-content--hidden'}`}
                    key={tab.key}
                >
                    {Array.from({ length: Math.ceil(tab.fields.length / 2) }).map((_, rowIdx) => (
                        <div className="frow" key={rowIdx}>
                            {tab.fields.slice(rowIdx * 2, rowIdx * 2 + 2).map((field) => {
                                const error = getNestedValue(errors, field.chave);
                                return (
                                    <div className="fgroup" key={field.chave}>
                                        <label>{getDocumentoFieldText(field, tipoDocumentoValue).label} {isFieldRequired(field) && <span className="req">*</span>}</label>
                                        {renderField(field, error)}
                                        {error && <p className="error">{error.message}</p>}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            ))}

            <StepNavigation
                onBack={goBack}
                onNext={goToNextTab}
                nextLabel={isLastTab ? 'Próximo' : `Próxima Etapa (${tabs[activeTabIndex + 1]?.label})`}
                nextLabelMobile={isLastTab ? undefined : 'Próximo'}
                nextLoading={isLastTab && isValidatingDigitacao}
            />
        </form>
    );
};

export default StepDigitacaoForm;
